import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Drawer,
  TextField,
  Snackbar,
  Alert,
  Chip,
  useTheme,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Autocomplete,
  Avatar,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';
import DatePicker from '../../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import MeetingRoomRoundedIcon from '@mui/icons-material/MeetingRoomRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import DeleteSweepRoundedIcon from '@mui/icons-material/DeleteSweepRounded';
import moment from 'moment';
import {
  getLostAndFound,
  createLostAndFound,
  updateLostAndFound,
  deleteLostAndFound,
  getCustomerList,
} from '../../services/api';
import { getConfigurationRooms, getHousekeepingStaff } from '../../services/api';
import { detectThemeSync } from '../../utils/themeUtils';
import {
  getHousekeepingDrawerFooterSx,
  getHousekeepingDrawerHeaderSx,
  getHousekeepingDrawerPaperSx,
  getHousekeepingListPaperSx,
  getHousekeepingThemeColors,
} from '../../utils/housekeepingMobileLayout';
import {
  createDrawerOverlayProps,
  getDrawerRootSx,
} from '../../utils/drawerOverlayProps';
import {
  getSoftFieldSx,
  SectionLabel,
  SelectableChip,
  DrawerHeaderTitle,
  getDrawerPrimaryButtonSx,
  getDrawerCancelButtonSx,
} from './housekeepingFormKit';

const THEME_COLOR = '#4ce1b6';

const STATUS_CHOICES = [
  { value: 'STORED', label: 'Stored', color: '#ff9800', icon: <Inventory2RoundedIcon /> },
  { value: 'RETURNED', label: 'Returned', color: '#4caf50', icon: <AssignmentTurnedInRoundedIcon /> },
  { value: 'DISPOSED', label: 'Disposed', color: '#78909c', icon: <DeleteSweepRoundedIcon /> },
];

const GUEST_SEARCH_DEBOUNCE_MS = 350;

const LostAndFoundSection = () => {
  const theme = useTheme();
  const isDark = detectThemeSync() === 'dark';
  const themeColors = getHousekeepingThemeColors(isDark);
  const drawerOverlays = useMemo(
    () => createDrawerOverlayProps(isDark, isDark ? themeColors.surface : '#ffffff'),
    [isDark, themeColors.surface]
  );
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [itemList, setItemList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [rooms, setRooms] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [guestSearchOptions, setGuestSearchOptions] = useState([]);
  const [guestSearchLoading, setGuestSearchLoading] = useState(false);

  const [formData, setFormData] = useState({
    room: '',
    found_by: '',
    item_description: '',
    status: 'STORED',
    guest_name: '',
    guest_contact: '',
    returned_date: null,
    disposed_date: null,
    notes: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchItems();
    fetchRooms();
    fetchStaff();
  }, []);

  const fetchRooms = async () => {
    try {
      const roomsData = await getConfigurationRooms();
      const allRooms = [];
      roomsData.forEach(floor => {
        if (floor.rooms && floor.rooms.length > 0) {
          floor.rooms.forEach(room => {
            allRooms.push({
              id: room.id,
              name: `${room.room_number} (${room.room_type_name})`,
              room_number: room.room_number,
              room_type_name: room.room_type_name || '',
              image_url: room.room_type_image_url || room.image_url || room.image || null,
            });
          });
        }
      });
      setRooms(allRooms);
    } catch (error) {}
  };

  const fetchStaff = async () => {
    try {
      const response = await getHousekeepingStaff({ page_size: 1000 });
      setStaffList(response.results || []);
    } catch (error) {}
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) {
        params.search = search;
      }
      params.page = page + 1;
      params.page_size = rowsPerPage;
      const response = await getLostAndFound(params);
      setItemList(response.results || []);
    } catch (error) {setSnackbar({
        open: true,
        message: error.userMessage || 'Failed to load lost and found items',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [page, rowsPerPage, search]);

  // Debounced guest/customer search by name (when drawer is open)
  useEffect(() => {
    if (!openDrawer) return;
    const query = (formData.guest_name || '').trim();
    if (!query || query.length < 2) {
      setGuestSearchOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      setGuestSearchLoading(true);
      try {
        const res = await getCustomerList({ name: query, per_page: 20, page: 1 });
        setGuestSearchOptions(res?.Data ?? []);
      } catch {
        setGuestSearchOptions([]);
      } finally {
        setGuestSearchLoading(false);
      }
    }, GUEST_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [openDrawer, formData.guest_name]);

  const handleOpenDrawer = (item = null) => {
    setGuestSearchOptions([]);
    if (item) {
      setEditingItem(item);
      const roomId = typeof item.room === 'object' && item.room != null ? item.room.id : (item.room_id ?? item.room ?? '');
      const foundById = typeof item.found_by === 'object' && item.found_by != null ? item.found_by.id : (item.found_by_id ?? item.found_by ?? '');
      setFormData({
        room: roomId === '' || roomId == null ? '' : (Number(roomId) || roomId),
        found_by: foundById === '' || foundById == null ? '' : (Number(foundById) || foundById),
        item_description: item.item_description || '',
        status: item.status || 'STORED',
        guest_name: item.guest_name || '',
        guest_contact: item.guest_contact || '',
        returned_date: item.returned_date ? moment(item.returned_date) : null,
        disposed_date: item.disposed_date ? moment(item.disposed_date) : null,
        notes: item.notes || '',
      });
    } else {
      setEditingItem(null);
      setFormData({
        room: '',
        found_by: '',
        item_description: '',
        status: 'STORED',
        guest_name: '',
        guest_contact: '',
        returned_date: null,
        disposed_date: null,
        notes: '',
      });
    }
    setErrors({});
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setEditingItem(null);
    setGuestSearchOptions([]);
    setFormData({
      room: '',
      found_by: '',
      item_description: '',
      status: 'STORED',
      guest_name: '',
      guest_contact: '',
      returned_date: null,
      disposed_date: null,
      notes: '',
    });
    setErrors({});
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.item_description.trim()) {
      newErrors.item_description = 'Item description is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      const dataToSend = {
        room: formData.room || null,
        found_by: formData.found_by || null,
        item_description: formData.item_description,
        status: formData.status,
        guest_name: formData.guest_name || '',
        guest_contact: formData.guest_contact || '',
        returned_date: formData.returned_date ? formData.returned_date.format('YYYY-MM-DD') : null,
        disposed_date: formData.disposed_date ? formData.disposed_date.format('YYYY-MM-DD') : null,
        notes: formData.notes,
      };

      if (editingItem) {
        await updateLostAndFound(editingItem.id, dataToSend);
        setSnackbar({
          open: true,
          message: 'Lost and found item updated successfully',
          severity: 'success',
        });
      } else {
        await createLostAndFound(dataToSend);
        setSnackbar({
          open: true,
          message: 'Lost and found item created successfully',
          severity: 'success',
        });
      }
      handleCloseDrawer();
      fetchItems();
    } catch (error) {setSnackbar({
        open: true,
        message: error.userMessage || 'Failed to save lost and found item',
        severity: 'error',
      });
    }
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteLostAndFound(itemToDelete.id);
      setSnackbar({
        open: true,
        message: 'Lost and found item deleted successfully',
        severity: 'success',
      });
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchItems();
    } catch (error) {setSnackbar({
        open: true,
        message: error.userMessage || 'Failed to delete lost and found item',
        severity: 'error',
      });
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredItems = useMemo(() => {
    if (!search) return itemList;
    const searchLower = search.toLowerCase();
    return itemList.filter(
      (item) =>
        item.item_description?.toLowerCase().includes(searchLower) ||
        item.guest_name?.toLowerCase().includes(searchLower) ||
        item.room?.room_number?.toLowerCase().includes(searchLower)
    );
  }, [itemList, search]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'RETURNED':
        return '#4caf50';
      case 'DISPOSED':
        return '#999';
      case 'STORED':
        return '#ff9800';
      default:
        return '#999';
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box>
        <Paper
          elevation={0}
        sx={{
          ...getHousekeepingListPaperSx(isDark),
          p: { xs: 1.5, sm: 2, md: 2.5 },
        }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 600 }}>
              Lost & Found
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDrawer()}
              sx={{
                backgroundColor: THEME_COLOR,
                color: '#000',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: THEME_COLOR,
                  opacity: 0.9,
                },
              }}
            >
              Add Item
            </Button>
          </Box>

          <TextField
            fullWidth
            placeholder="Search by item description, guest name, or room number..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: <FindInPageIcon sx={{ mr: 1, color: isDark ? '#999' : '#666' }} />,
            }}
          />

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Item Description</TableCell>
                      <TableCell>Room</TableCell>
                      <TableCell>Found By</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Guest Name</TableCell>
                      <TableCell>Found Date</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body2" sx={{ color: isDark ? '#999' : '#666', py: 2 }}>
                            No lost and found items found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredItems.map((item) => (
                        <TableRow key={item.id} hover>
                          <TableCell>{item.item_description || 'N/A'}</TableCell>
                          <TableCell>{item.room_number || item.room?.room_number || 'N/A'}</TableCell>
                          <TableCell>{item.found_by_name || item.found_by?.full_name || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip
                              label={STATUS_CHOICES.find((s) => s.value === item.status)?.label || item.status}
                              size="small"
                              sx={{
                                backgroundColor: getStatusColor(item.status),
                                color: '#fff',
                              }}
                            />
                          </TableCell>
                          <TableCell>{item.guest_name || 'N/A'}</TableCell>
                          <TableCell>{item.found_date ? moment(item.found_date).format('DD/MM/YYYY') : 'N/A'}</TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDrawer(item)}
                              sx={{ color: THEME_COLOR }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(item)}
                              sx={{ color: '#f44336' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredItems.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </Paper>

        {/* Add/Edit Drawer */}
        <Drawer
          anchor="right"
          open={openDrawer}
          onClose={handleCloseDrawer}
          sx={getDrawerRootSx()}
          PaperProps={{
            sx: {
              width: { xs: '100%', sm: 600 },
              ...getHousekeepingDrawerPaperSx(isDark),
              backgroundColor: isDark ? themeColors.surface : '#ffffff',
              backgroundImage: 'none',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            },
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              ...getHousekeepingDrawerHeaderSx(isDark),
              backgroundColor: isDark ? themeColors.headerBg : '#ffffff',
              backgroundImage: 'none',
            }}
          >
            <DrawerHeaderTitle
              icon={<FindInPageIcon sx={{ fontSize: 22 }} />}
              title={editingItem ? 'Edit Lost & Found Item' : 'Add Lost & Found Item'}
              subtitle={editingItem ? 'Update the details below' : 'Log an item found in the hotel'}
              isDark={isDark}
            />
            <IconButton
              onClick={handleCloseDrawer}
              sx={{
                color: isDark ? '#999' : '#666',
                backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              label="Item Description"
              fullWidth
              required
              value={formData.item_description}
              onChange={(e) => handleInputChange('item_description', e.target.value)}
              error={!!errors.item_description}
              helperText={errors.item_description}
              sx={getSoftFieldSx(isDark)}
            />

            <FormControl fullWidth sx={getSoftFieldSx(isDark)}>
              <InputLabel>Room</InputLabel>
              <Select
                value={formData.room}
                onChange={(e) => handleInputChange('room', e.target.value)}
                label="Room"
                renderValue={(selected) => {
                  if (!selected) return 'Not specified';
                  const room = rooms.find((r) => r.id === selected || String(r.id) === String(selected));
                  if (!room) return '';
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Avatar
                        src={room.image_url || undefined}
                        variant="rounded"
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: isDark ? '#1f2a28' : '#e6f9f2',
                          color: THEME_COLOR,
                        }}
                      >
                        <MeetingRoomRoundedIcon fontSize="small" />
                      </Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {room.name}
                      </Typography>
                    </Box>
                  );
                }}
                MenuProps={{
                  ...drawerOverlays.selectMenuProps,
                  PaperProps: {
                    ...drawerOverlays.selectMenuProps.PaperProps,
                    sx: {
                      ...drawerOverlays.selectMenuProps.PaperProps?.sx,
                      maxHeight: 360,
                    },
                  },
                }}
              >
                <MenuItem value="">Not specified</MenuItem>
                {rooms.map((room) => (
                  <MenuItem
                    key={room.id}
                    value={room.id}
                    sx={{
                      py: 1,
                      gap: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.1)',
                      },
                      '&.Mui-selected:hover': {
                        backgroundColor: isDark ? 'rgba(76, 225, 182, 0.18)' : 'rgba(76, 225, 182, 0.16)',
                      },
                    }}
                  >
                    <ListItemAvatar sx={{ minWidth: 48 }}>
                      <Avatar
                        src={room.image_url || undefined}
                        variant="rounded"
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: isDark ? '#1f2a28' : '#e6f9f2',
                          color: THEME_COLOR,
                        }}
                      >
                        <MeetingRoomRoundedIcon fontSize="small" />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={room.room_number}
                      secondary={room.room_type_name}
                      primaryTypographyProps={{ fontWeight: 700, fontSize: '0.95rem' }}
                      secondaryTypographyProps={{
                        fontSize: '0.75rem',
                        color: isDark ? '#8a939e' : '#78828c',
                      }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={getSoftFieldSx(isDark)}>
              <InputLabel>Found By</InputLabel>
              <Select
                value={formData.found_by}
                onChange={(e) => handleInputChange('found_by', e.target.value)}
                label="Found By"
                MenuProps={drawerOverlays.selectMenuProps}
              >
                <MenuItem value="">Not specified</MenuItem>
                {staffList.map((staff) => (
                  <MenuItem key={staff.id} value={staff.id}>
                    {staff.full_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Status — selectable chips */}
            <Box>
              <SectionLabel isDark={isDark}>Status</SectionLabel>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {STATUS_CHOICES.map((status) => (
                  <SelectableChip
                    key={status.value}
                    label={status.label}
                    icon={status.icon}
                    selected={formData.status === status.value}
                    color={status.color}
                    isDark={isDark}
                    onClick={() => handleInputChange('status', status.value)}
                  />
                ))}
              </Box>
            </Box>

            <Autocomplete
              freeSolo
              options={guestSearchOptions}
              loading={guestSearchLoading}
              inputValue={formData.guest_name}
              onInputChange={(_, value) => handleInputChange('guest_name', value)}
              onChange={(_, option) => {
                if (option && typeof option === 'object' && option.name) {
                  setFormData((prev) => ({
                    ...prev,
                    guest_name: option.name || prev.guest_name,
                    guest_contact: option.phone || prev.guest_contact,
                  }));
                  if (errors.guest_name) setErrors((prev) => ({ ...prev, guest_name: null }));
                }
              }}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option?.name ?? '')}
              slotProps={drawerOverlays.autocompleteSlotProps}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{option.name}</Typography>
                    {option.phone && (
                      <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                        {option.phone}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Guest Name"
                  placeholder="Search by name from customer records..."
                  sx={getSoftFieldSx(isDark)}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {guestSearchLoading ? <CircularProgress color="inherit" size={20} sx={{ mr: 1 }} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <TextField
              label="Guest Contact (auto-filled when guest selected)"
              fullWidth
              value={formData.guest_contact}
              onChange={(e) => handleInputChange('guest_contact', e.target.value)}
              placeholder="Phone / number"
              sx={getSoftFieldSx(isDark)}
            />

            {formData.status === 'RETURNED' && (
              <DatePicker
                label="Returned Date"
                value={formData.returned_date}
                onChange={(date) => handleInputChange('returned_date', date)}
                slotProps={{
                  ...drawerOverlays.datePickerSlotProps,
                  textField: {
                    fullWidth: true,
                    sx: getSoftFieldSx(isDark),
                  },
                }}
              />
            )}

            {formData.status === 'DISPOSED' && (
              <DatePicker
                label="Disposed Date"
                value={formData.disposed_date}
                onChange={(date) => handleInputChange('disposed_date', date)}
                slotProps={{
                  ...drawerOverlays.datePickerSlotProps,
                  textField: {
                    fullWidth: true,
                    sx: getSoftFieldSx(isDark),
                  },
                }}
              />
            )}

            <TextField
              label="Notes"
              fullWidth
              multiline
              rows={4}
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              sx={getSoftFieldSx(isDark)}
            />
          </Box>

          {/* Footer */}
          <Box
            sx={{
              p: 1.5,
              ...getHousekeepingDrawerFooterSx(isDark),
              backgroundColor: isDark ? themeColors.headerBg : '#ffffff',
              backgroundImage: 'none',
              display: 'flex',
              gap: 1.5,
            }}
          >
            <Button
              variant="outlined"
              fullWidth
              onClick={handleCloseDrawer}
              sx={getDrawerCancelButtonSx(isDark, themeColors.surfaceBorder)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSave}
              sx={getDrawerPrimaryButtonSx()}
            >
              {editingItem ? 'Update' : 'Add Lost & Found'}
            </Button>
          </Box>
        </Drawer>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Lost & Found Item</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this lost and found item? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default LostAndFoundSection;

